import { useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { KeyRound, Eye, EyeOff, Loader2, X } from "lucide-react";
import api, { apiError, passwordError } from "../../lib/api";

// Super admin sets a new password for a user via the upstream backend.
export function SetPasswordModal({ user, onClose }) {
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async (e) => {
    e.preventDefault();
    if (pw !== pw2) return setErr("The passwords don't match.");
    const pe = passwordError(pw); if (pe) return setErr(pe);
    setBusy(true); setErr("");
    try {
      await api.post(`/admin/users/${user.id}/set-password`, { new_password: pw });
      toast.success(`New password set for ${user.name}`);
      onClose();
    } catch (ex) {
      const st = ex?.response?.status;
      setErr(st === 404 || st === 405 ? "The backend doesn't support setting passwords yet. Ask your backend developer to add POST /api/admin/users/{id}/set-password." : apiError(ex));
    } finally { setBusy(false); }
  };
  const inp = "w-full bg-[#0B1220] border border-slate-700 text-slate-100 rounded-lg px-3 py-2.5 text-sm pr-10 focus:border-amber-500 focus:outline-none";
  return createPortal(
    <div className="fixed inset-0 z-[70] bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <form onSubmit={save} onClick={(e) => e.stopPropagation()} data-testid="set-password-modal" className="w-full max-w-md bg-[#0F172A] border border-slate-700 rounded-2xl p-5 relative">
        <button type="button" onClick={onClose} className="absolute top-4 right-4 text-slate-400" data-testid="set-password-close"><X size={18} /></button>
        <div className="flex items-center gap-2"><KeyRound className="text-amber-500" size={20} /><h2 className="text-lg font-extrabold text-white">Set new password</h2></div>
        <p className="text-sm text-slate-400 mt-1">For <span className="font-semibold text-slate-200">{user.name}</span> ({user.email}). Share it with them safely and ask them to change it.</p>
        <label className="block text-xs font-bold text-slate-400 uppercase mt-4 mb-1.5">New password</label>
        <div className="relative">
          <input type={show ? "text" : "password"} autoComplete="new-password" value={pw} onChange={(e) => setPw(e.target.value)} className={inp} data-testid="set-password-input" autoFocus />
          <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400" data-testid="set-password-toggle">{show ? <EyeOff size={18} /> : <Eye size={18} />}</button>
        </div>
        <label className="block text-xs font-bold text-slate-400 uppercase mt-3 mb-1.5">Confirm new password</label>
        <input type={show ? "text" : "password"} autoComplete="new-password" value={pw2} onChange={(e) => setPw2(e.target.value)} className={inp} data-testid="set-password-confirm" />
        <p className="text-xs text-slate-500 mt-2">At least 10 characters, with letters and numbers.</p>
        {err && <p className="text-sm text-red-400 mt-3" data-testid="set-password-error">{err}</p>}
        <button disabled={busy} className="w-full bg-amber-500 hover:bg-amber-600 text-slate-950 font-bold rounded-lg py-2.5 mt-4 flex items-center justify-center disabled:opacity-60" data-testid="set-password-save">
          {busy ? <Loader2 size={18} className="animate-spin" /> : "Save password"}
        </button>
      </form>
    </div>,
    document.body
  );
}
